// src/hooks/useTrialMatches.ts
import { useCallback, useRef, useState } from "react";
import { createData, fetchData } from "api/apiHelpers";
import logger from "utils/logger";

export type EligibilityVerdict = "likely" | "unlikely" | "unclear";

// Global ranking used to keep the accumulated list sorted across "Search more"
// pages. Mirrors the backend comparator (matcher.py): likely → unclear →
// unlikely, then by confidence descending. Without this, a later page's
// "likely" trial would sit below the first page's "unlikely" ones because the
// backend only sorts within a single round and the frontend appends pages.
const VERDICT_ORDER: Record<string, number> = { likely: 0, unclear: 1, unlikely: 2 };
function sortMatchesByLikelihood(list: TrialMatch[]): TrialMatch[] {
  return [...list].sort(
    (a, b) =>
      (VERDICT_ORDER[a.eligible] ?? 1) - (VERDICT_ORDER[b.eligible] ?? 1) ||
      (b.confidence ?? 0) - (a.confidence ?? 0),
  );
}

export interface TrialLocation {
  facility: string;
  city: string;
  country: string;
}

export interface TrialMatch {
  nct_id: string;
  title: string;
  brief_title: string;
  phase: string;
  status: string;
  sponsor: string;
  url: string;
  conditions: string[];
  eligibility_criteria: string;
  locations: TrialLocation[];

  eligible: EligibilityVerdict;
  confidence: number;
  met_criteria: string[];
  unmet_criteria: string[];
  unclear_criteria: string[];
  reasoning: string;

  is_enrolled: boolean;
}

interface TaskEnqueuedResponse {
  task_id: string;
  status: string;
}

/** The effective ClinicalTrials.gov query the matcher ran (cursor_logs/048).
 *  Surfaced so the panel can show it and let the user edit + re-run. Free-form
 *  key→value; the editable header exposes a curated subset. */
export type TrialSearchParams = Record<string, string>;

interface TaskStatusResponse {
  task_id: string;
  status: "pending" | "running" | "completed" | "error" | string;
  error?: string;
  patient_id?: number;
  count?: number;
  matches?: TrialMatch[];
  candidates_evaluated?: number;
  assessments_returned?: number;
  search_params?: TrialSearchParams;
}

interface EnrollResponse {
  id: number;
  patient_id: number;
  nct_id: string;
  trial_name: string;
  enrollment_status: string;
  message: string;
}

const POLL_INTERVAL_MS = 3_000;
const MAX_POLL_ATTEMPTS = 60; // 3 min total
const PENDING_WARN_AFTER = 5; // warn after 5 polls (15s) still PENDING

export interface EvaluationMeta {
  candidatesEvaluated: number;
  assessmentsReturned: number;
}

export function useTrialMatches(patientId: number | undefined) {
  const [matches, setMatches] = useState<TrialMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [evaluationMeta, setEvaluationMeta] = useState<EvaluationMeta | null>(null);
  // The effective CT.gov query of the last run (cursor_logs/048) — prefills the
  // editable search-params header so the user can correct + re-run.
  const [searchParams, setSearchParams] = useState<TrialSearchParams | null>(null);
  const cancelRef = useRef(false);

  const findMatches = useCallback(
    async (options?: {
      top_n?: number;
      location?: string;
      exclude_nct_ids?: string[];
      append?: boolean;
      /** User-edited CT.gov params; when set the backend skips the LLM and
       *  runs them verbatim (location stays owned by the `location` field). */
      search_params?: TrialSearchParams;
    }) => {
      if (!patientId) return;
      cancelRef.current = false;
      setLoading(true);
      setError(null);
      setWarning(null);
      setHasSearched(true);

      const appendMode = options?.append ?? false;

      try {
        const { task_id } = await createData<
          {
            patient_id: number;
            top_n: number;
            location?: string;
            exclude_nct_ids?: string[];
            search_params?: TrialSearchParams;
          },
          TaskEnqueuedResponse
        >("/trials/matches/", {
          patient_id: patientId,
          ...(options?.search_params
            ? { search_params: options.search_params }
            : {}),
          // 30 per round (batch size 5 × 3 workers = 2 parallel waves, ~35s,
          // well under the 180s poll ceiling). Raised from 15 once the stale
          // 127s/30-trials figure was disproven on the gpt-4.1-mini fast
          // deployment. See cursor_logs/022 §12.16. "Search more" accumulates
          // and the list is globally re-sorted by likelihood.
          top_n: options?.top_n || 30,
          // `""` is MEANINGFUL: it is the user saying worldwide. Only an
          // absent location lets the backend fall back to the org default.
          ...(options?.location !== undefined
            ? { location: options.location }
            : {}),
          ...(options?.exclude_nct_ids?.length
            ? { exclude_nct_ids: options.exclude_nct_ids }
            : {}),
        });

        let attempts = 0;
        let everStarted = false;
        while (attempts < MAX_POLL_ATTEMPTS) {
          if (cancelRef.current) return;
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (cancelRef.current) return;

          const statusResp = await fetchData<TaskStatusResponse>(
            `/trials/matches/${task_id}/`
          );

          if (statusResp.status === "completed") {
            setWarning(null);
            if (statusResp.search_params) setSearchParams(statusResp.search_params);
            const newMatches = statusResp.matches ?? [];
            setMatches((prev) =>
              sortMatchesByLikelihood(
                appendMode ? [...prev, ...newMatches] : newMatches
              )
            );
            const evaluated = statusResp.candidates_evaluated ?? 0;
            const returned = statusResp.assessments_returned ?? 0;
            if (evaluated > 0) {
              setEvaluationMeta((prev) =>
                appendMode && prev
                  ? {
                      candidatesEvaluated: prev.candidatesEvaluated + evaluated,
                      assessmentsReturned: prev.assessmentsReturned + returned,
                    }
                  : { candidatesEvaluated: evaluated, assessmentsReturned: returned }
              );
            }
            return;
          }
          if (statusResp.status === "error") {
            throw new Error(statusResp.error || "Trial matching failed");
          }
          if (statusResp.status === "running") {
            everStarted = true;
            setWarning(null);
          }
          if (
            statusResp.status === "pending" &&
            !everStarted &&
            attempts >= PENDING_WARN_AFTER
          ) {
            setWarning(
              "The task is queued but hasn't started yet. " +
                "This may indicate a temporary connectivity issue — it will retry automatically."
            );
          }
          attempts++;
        }
        throw new Error("Trial matching timed out — please try again");
      } catch (err) {
        if (cancelRef.current) return;
        const msg =
          err instanceof Error ? err.message : "Failed to find matches";
        setError(msg);
        logger.error("[useTrialMatches] Error:", msg);
      } finally {
        if (!cancelRef.current) {
          setLoading(false);
          setWarning(null);
        }
      }
    },
    [patientId]
  );

  const enrollPatient = useCallback(
    async (nctId: string): Promise<EnrollResponse | null> => {
      if (!patientId) return null;
      try {
        const result = await createData<
          { patient_id: number; nct_id: string },
          EnrollResponse
        >("/trials/enroll/", {
          patient_id: patientId,
          nct_id: nctId,
        });
        setMatches((prev) =>
          prev.map((m) =>
            m.nct_id === nctId ? { ...m, is_enrolled: true } : m
          )
        );
        return result;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to enroll patient";
        logger.error("[useTrialMatches] Enroll error:", msg);
        throw err;
      }
    },
    [patientId]
  );

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  return {
    matches,
    loading,
    error,
    warning,
    hasSearched,
    evaluationMeta,
    searchParams,
    findMatches,
    enrollPatient,
    cancel,
  };
}
